import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth, authFetch } from "@/App";
import FeedCard from "@/components/FeedCard";
import AlbumCard from "@/components/AlbumCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Disc3, TrendingUp, Sparkles, ChevronRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [trending, setTrending] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("feed");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [feedRes, discoverRes, trendingRes, releasesRes] = await Promise.all([
        authFetch(`${API}/feed`),
        authFetch(`${API}/discover`),
        authFetch(`${API}/deezer/charts?limit=8`),
        authFetch(`${API}/deezer/new-releases?limit=8`),
      ]);

      if (feedRes.ok) {
        const feedData = await feedRes.json();
        setFeed(feedData);
      }

      if (discoverRes.ok) {
        const discoverData = await discoverRes.json();
        setDiscover(discoverData);
      }

      if (trendingRes.ok) {
        const trendingData = await trendingRes.json();
        setTrending(trendingData);
      }

      if (releasesRes.ok) {
        const releasesData = await releasesRes.json();
        setNewReleases(releasesData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36 pb-12" data-testid="dashboard-page">
      {/* Purple gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-transparent to-purple-900/10 pointer-events-none" />
      
      <div className="relative max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-white">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-white/60">
            See what you and your friends have been listening to.
          </p>
        </div>

        {/* Trending Section */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              <h2 className="text-xl font-semibold text-white">Trending Now</h2>
            </div>
            <Link to="/discover">
              <Button variant="ghost" size="sm" className="text-white/60 hover:text-white gap-1">
                See all <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square rounded-lg bg-white/5 mb-2" />
                  <div className="w-3/4 h-3 bg-white/5 rounded mb-1" />
                  <div className="w-1/2 h-2 bg-white/5 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {trending.slice(0, 4).map((album) => (
                <AlbumCard key={album.album_id} album={album} compact />
              ))}
            </div>
          )}
        </section>

        {/* New Releases Section */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <h2 className="text-xl font-semibold text-white">New Releases</h2>
            </div>
            <Link to="/discover">
              <Button variant="ghost" size="sm" className="text-white/60 hover:text-white gap-1">
                See all <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square rounded-lg bg-white/5 mb-2" />
                  <div className="w-3/4 h-3 bg-white/5 rounded mb-1" />
                  <div className="w-1/2 h-2 bg-white/5 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {newReleases.slice(0, 4).map((album) => (
                <AlbumCard key={album.album_id} album={album} compact />
              ))}
            </div>
          )}
        </section>

        {/* Activity Feed Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-2xl">
          <TabsList className="w-full bg-muted/50 p-1 rounded-full mb-6">
            <TabsTrigger 
              value="feed" 
              className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="feed-tab"
            >
              Your Feed
            </TabsTrigger>
            <TabsTrigger 
              value="discover"
              className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="discover-tab"
            >
              Community
            </TabsTrigger>
          </TabsList>

          <TabsContent value="feed" className="space-y-6">
            {loading ? (
              <LoadingSkeleton />
            ) : feed.length > 0 ? (
              feed.map((log) => (
                <FeedCard key={log.log_id} log={log} />
              ))
            ) : (
              <EmptyState 
                title="Your feed is empty"
                description="Follow some users or log your first album to see activity here."
              />
            )}
          </TabsContent>

          <TabsContent value="discover" className="space-y-6">
            {loading ? (
              <LoadingSkeleton />
            ) : discover.length > 0 ? (
              discover.map((log) => (
                <FeedCard key={log.log_id} log={log} />
              ))
            ) : (
              <EmptyState 
                title="No activity yet"
                description="Be the first to log an album!"
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="glass-card rounded-xl p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-white/5" />
            <div className="flex-1">
              <div className="w-32 h-4 bg-white/5 rounded mb-2" />
              <div className="w-20 h-3 bg-white/5 rounded" />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-24 h-24 bg-white/5 rounded-lg" />
            <div className="flex-1">
              <div className="w-3/4 h-5 bg-white/5 rounded mb-2" />
              <div className="w-1/2 h-4 bg-white/5 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="glass-card rounded-xl p-12 text-center">
      <Disc3 className="w-12 h-12 text-white/30 mx-auto mb-4" />
      <h3 className="text-lg font-semibold mb-2 text-white">{title}</h3>
      <p className="text-white/60">{description}</p>
    </div>
  );
}
